// ── BCHNostr — Time Capsules ──────────────────────────────────────
//
// A Time Capsule is a text message that CANNOT be read — by anyone,
// including the author and the BCHNostr operator — until a chosen future
// date arrives. When that moment comes it unlocks for everyone the capsule
// was addressed to, with no action required from any server.
//
// ── Why there is no "backend that releases the key" ──────────────────
// The original product spec imagined a server that stores each capsule's
// decryption key and publishes it on the unlock date. BCHNostr has no such
// always-on server, and more importantly that design is *insecure*: whoever
// runs the server (or breaches it) could read every sealed message early.
//
// Instead we use TIMELOCK ENCRYPTION via drand (https://drand.love), the
// same scheme the drand team's "timevault" demo uses. drand's League of
// Entropy publishes verifiable randomness on a fixed 3-second schedule.
// tlock-js encrypts the message so that the symmetric key can ONLY be
// reconstructed once drand has emitted the randomness for a future "round"
// (i.e. a future point in time). Until that round is reached the ciphertext
// is mathematically un-openable; after it, ANY client can decrypt with no
// key custodian, no operator trust, and no per-capsule server state.
//
// So the flow is:
//   1. Generate the message, pick an unlock date.
//   2. Charge $0.99 in BCH to the platform wallet (see platform.ts).
//   3. ONLY after payment: timelock-encrypt the message to the drand round
//      that lands on the unlock date, entirely in the browser.
//   4. Publish ONLY the ciphertext to Nostr (kind 34781). The plaintext
//      never leaves the browser and is never stored anywhere.
//   5. Before unlock: show metadata + a live countdown, message unreadable.
//   6. At/after unlock: tlock-js fetches the now-available drand beacon and
//      decrypts locally. Nothing is "released" by us — time itself is the key.
//
// Everything in a capsule event is UNTRUSTED even though it is signed. This
// module is the single sanitising boundary for capsule metadata.

import type { Event } from 'nostr-tools';
import { PLATFORM_FEE_ADDRESS } from './platform';
import { stripHtml } from './podcast';

// ── Kind & tags ────────────────────────────────────────────────────

/**
 * Addressable time-capsule event (kind 34781).
 *
 * Addressable (not a regular kind) so the author can correct a typo in the
 * public title/metadata without minting a second capsule — the sealed
 * ciphertext and unlock round are fixed at creation and never change.
 */
export const CAPSULE_KIND = 34781;

/** Relay-indexable discovery tag every capsule carries. */
export const CAPSULE_TAG = 'bch-time-capsule';

/** d-tag prefix — a random id per capsule. */
export const CAPSULE_D_PREFIX = 'bchnostr/capsule/';

/** Payment for sealing a capsule goes to the platform wallet. */
export const CAPSULE_WALLET = PLATFORM_FEE_ADDRESS;

// ── Pricing ────────────────────────────────────────────────────────
// Standard text capsule: $0.99. The BCH amount is computed from the live
// price at purchase time and shown to the user before they pay.
//
// Tiers are declared as data so premium options (longer messages, media,
// featured public capsules, gifts, custom designs) can be layered on later
// without touching the encryption or payment code.

export type CapsuleTier = 'standard';

export interface CapsuleTierSpec {
  id: CapsuleTier;
  label: string;
  priceUsd: number;
  /** Max plaintext length (characters). */
  maxChars: number;
  /** Max seal duration in seconds (how far in the future the unlock may be). */
  maxDurationSecs: number;
}

export const CAPSULE_TIERS: Record<CapsuleTier, CapsuleTierSpec> = {
  standard: {
    id: 'standard',
    label: 'Standard',
    priceUsd: 0.50,
    maxChars: 2000,
    // Up to 5 years out. drand mainnet is expected to run well beyond this;
    // a longer cap can be offered as a premium tier later.
    maxDurationSecs: 5 * 365 * 24 * 3600,
  },
};

/** Convert a USD price to sats given the current BCH price. Null if price unknown. */
export function usdToSats(usd: number, bchPriceUsd: number | null): number | null {
  if (!bchPriceUsd || bchPriceUsd <= 0) return null;
  return Math.ceil((usd / bchPriceUsd) * 1e8);
}

/** Human-readable BCH amount (8 dp, trimmed) for a sats value. */
export function satsToBchStr(sats: number): string {
  const bch = sats / 1e8;
  // Trim trailing zeros but keep it obviously a BCH amount.
  return bch.toFixed(8).replace(/0+$/, '').replace(/\.$/, '.0');
}

// ── Visibility ─────────────────────────────────────────────────────

export type CapsuleVisibility = 'private' | 'addressed' | 'public';

// ── Shape ──────────────────────────────────────────────────────────

/** A time capsule as published — all fields already sanitised. */
export interface TimeCapsule {
  /** The event id. */
  id: string;
  /** The capsule's `d` tag. */
  dTag: string;
  /** Author pubkey. */
  pubkey: string;
  /** Public, always-readable title. Plain text. */
  title: string;
  visibility: CapsuleVisibility;
  /** Recipient pubkey (hex) when visibility === 'addressed'. */
  recipient?: string;
  /** Unix seconds the capsule unlocks. */
  unlockAt: number;
  /** Unix seconds the capsule was created. */
  createdAt: number;
  /** drand round the message is timelocked to. */
  round: number;
  /** The armored tlock ciphertext (opaque until unlock). */
  ciphertext: string;
  tier: CapsuleTier;
  /** Payment txid, when present (proof the seal was paid for). */
  txid?: string;
}

// ── drand mainnet parameters ───────────────────────────────────────
// Public constants for drand's "quicknet" RFC mainnet used by tlock-js's
// mainnetClient(). Genesis + 3s period let us compute the round for any
// future timestamp WITHOUT a network round-trip, so the compose UI can show
// the exact unlock time instantly. tlock-js re-verifies against the live
// chain when it actually encrypts/decrypts.
export const DRAND_GENESIS_SECS = 1692803367;
export const DRAND_PERIOD_SECS = 3;

/** The drand round whose beacon is emitted at (or just after) `unixSecs`. */
export function roundForTime(unixSecs: number): number {
  if (unixSecs <= DRAND_GENESIS_SECS) return 1;
  // Rounds are 1-indexed at genesis; round N is emitted at genesis+(N-1)*period.
  return Math.ceil((unixSecs - DRAND_GENESIS_SECS) / DRAND_PERIOD_SECS) + 1;
}

/** Approx unix seconds a given round is emitted. */
export function timeForRound(round: number): number {
  return DRAND_GENESIS_SECS + (round - 1) * DRAND_PERIOD_SECS;
}

// ── Validation helpers ─────────────────────────────────────────────

const HEX64 = /^[0-9a-f]{64}$/i;

function sanitizeTitle(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return stripHtml(raw, 140).trim();
}

function sanitizeVisibility(raw: unknown): CapsuleVisibility {
  return raw === 'private' || raw === 'addressed' || raw === 'public' ? raw : 'public';
}

/** Clamp an untrusted unix-seconds value to something sane. */
function sanitizeUnlock(raw: unknown, createdAt: number): number {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(n)) return createdAt;
  // Never before creation; never absurdly far out (2 * standard cap).
  const max = createdAt + CAPSULE_TIERS.standard.maxDurationSecs * 2;
  return Math.min(Math.max(Math.floor(n), createdAt), max);
}

// ── Parsing ─────────────────────────────────────────────────────────

/**
 * Parse a raw kind-34781 event into a sanitised TimeCapsule, or null if the
 * event is malformed. Never throws.
 */
export function parseCapsule(ev: Event): TimeCapsule | null {
  try {
    if (!ev || ev.kind !== CAPSULE_KIND || !Array.isArray(ev.tags)) return null;

    const tag = (name: string): string | undefined =>
      ev.tags.find(t => t[0] === name)?.[1];

    const dTag = tag('d');
    if (!dTag || !dTag.startsWith(CAPSULE_D_PREFIX)) return null;

    const ciphertext = ev.content;
    if (typeof ciphertext !== 'string' || ciphertext.length < 16) return null;

    const createdAt = ev.created_at;
    const unlockAt = sanitizeUnlock(tag('unlock'), createdAt);
    const roundRaw = parseInt(tag('round') || '0', 10);
    const round = Number.isFinite(roundRaw) && roundRaw > 0 ? roundRaw : roundForTime(unlockAt);

    const visibility = sanitizeVisibility(tag('visibility'));

    let recipient: string | undefined;
    if (visibility === 'addressed') {
      const p = tag('p');
      if (p && HEX64.test(p)) recipient = p.toLowerCase();
    }

    const txid = tag('txid');

    return {
      id: ev.id,
      dTag,
      pubkey: ev.pubkey,
      title: sanitizeTitle(tag('title')),
      visibility,
      recipient,
      unlockAt,
      createdAt,
      round,
      ciphertext,
      tier: 'standard',
      txid: txid && /^[0-9a-f]{64}$/i.test(txid) ? txid.toLowerCase() : undefined,
    };
  } catch {
    return null;
  }
}

/** Is the capsule's unlock moment in the past? (i.e. it can be opened) */
export function isUnlockable(capsule: Pick<TimeCapsule, 'unlockAt'>): boolean {
  return Math.floor(Date.now() / 1000) >= capsule.unlockAt;
}

/** Build a fresh random d-tag id for a new capsule. */
export function newCapsuleDTag(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const id = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  return CAPSULE_D_PREFIX + id;
}

// ── Timelock encrypt / decrypt (runtime-loaded tlock-js) ────────────
// tlock-js pins the v1 line of @noble/hashes / bls12-381, while the rest of
// the app (nostr-tools) is on the v2 line. Bundling both through the shared
// dependency graph makes esm.sh unify the noble packages and breaks — the v1
// `sha256` subpath tlock imports no longer exists in v2. So we load tlock-js
// at RUNTIME from esm.sh with `bundle=true`, giving it a fully self-contained
// copy of its own deps that never collides with the app's crypto. This is the
// same runtime-esm.sh pattern the app already uses for WizardConnect/hls.js,
// and it's lazy: the list view and countdowns never pull the heavy bls code —
// only the actual seal (create) and open (reveal) actions do.

const TLOCK_URL = 'https://esm.sh/tlock-js@0.8.0?target=esnext&bundle=true';

interface TlockModule {
  timelockEncrypt: (round: number, payload: unknown, client: unknown) => Promise<string>;
  timelockDecrypt: (ciphertext: string, client: unknown) => Promise<{ toString: (enc: string) => string }>;
  mainnetClient: () => { chain: () => { info: () => Promise<{ genesis_time: number; period: number }> } };
  roundAt: (timeMs: number, chainInfo: { genesis_time: number; period: number }) => number;
  Buffer: { from: (input: string, enc: string) => unknown };
}

let _tlock: Promise<TlockModule> | null = null;
function loadTlock(): Promise<TlockModule> {
  if (!_tlock) {
    _tlock = import(/* @vite-ignore */ TLOCK_URL) as Promise<TlockModule>;
  }
  return _tlock;
}

/**
 * Timelock-encrypt `message` so it can only be decrypted once the drand
 * round covering `unlockAt` has been emitted. Returns the armored ciphertext
 * and the round used. Runs entirely in the browser.
 */
export async function sealCapsule(
  message: string,
  unlockAt: number,
): Promise<{ ciphertext: string; round: number }> {
  const { timelockEncrypt, mainnetClient, roundAt, Buffer } = await loadTlock();
  const client = mainnetClient();
  // Ask the live chain for its parameters and compute the exact round, so we
  // never drift from the network's real schedule.
  const chainInfo = await client.chain().info();
  const round = roundAt(unlockAt * 1000, chainInfo);
  const ciphertext = await timelockEncrypt(round, Buffer.from(message, 'utf8'), client);
  return { ciphertext, round };
}

/**
 * Attempt to timelock-decrypt a capsule. Throws if the unlock round hasn't
 * been reached yet (the message is still sealed) or the ciphertext is bad.
 */
export async function openCapsule(ciphertext: string): Promise<string> {
  const { timelockDecrypt, mainnetClient } = await loadTlock();
  const client = mainnetClient();
  const plaintextBuf = await timelockDecrypt(ciphertext, client);
  // tlock returns a Buffer; toString('utf8') gives back the original message.
  return plaintextBuf.toString('utf8');
}
